import * as React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

interface PatientInfo {
  first_name: string;
  last_name: string;
  dob: string;
  gender?: string | null;
  phone?: string | null;
  insurance_carrier?: string | null;
}

interface Test {
  test_type: string;
  test_description: string;
  test_reason?: string;
  notes?: string;
  evidence?: string;
  body_part?: string | null;
  laterality?: string | null;
  contrast?: string | null;
  icd_code?: string | null;
}

interface Confidence {
  overall: number;
  physician_name: number;
  patient_info: number;
  tests: number;
}

interface ExtractedData {
  physician_name: string | null;
  physician_title?: string | null;
  signature: boolean;
  patient_info: PatientInfo;
  order_date: string;
  tests: Test[];
  results_explanation?: string;
  confidence: Confidence;
  warnings: string[];
  document_type?: string;
  facility_name?: string | null;
  physician_npi?: string | null;
  physician_phone?: string | null;
  icd_codes?: string[];
  additional_info?: string[];
}

interface OcrCorrectionFormProps {
  /**
   * Initial OCR data to edit
   */
  initialData: ExtractedData;
  /**
   * Callback when user saves corrections
   */
  onSave: (correctedData: ExtractedData, comments: string) => void;
  /**
   * Callback when user cancels editing
   */
  onCancel: () => void;
  /**
   * Whether the form is submitting
   */
  loading?: boolean;
}

/**
 * Editable form for correcting OCR extraction results
 * Allows users to modify physician info, patient info, tests, and add comments
 */
function OcrCorrectionForm({
  initialData,
  onSave,
  onCancel,
  loading = false,
}: OcrCorrectionFormProps) {
  // Form state
  const [physicianName, setPhysicianName] = React.useState(
    initialData.physician_name || ""
  );
  const [physicianTitle, setPhysicianTitle] = React.useState(
    initialData.physician_title || ""
  );
  const [physicianNpi, setPhysicianNpi] = React.useState(
    initialData.physician_npi || ""
  );
  const [physicianPhone, setPhysicianPhone] = React.useState(
    initialData.physician_phone || ""
  );
  const [facilityName, setFacilityName] = React.useState(
    initialData.facility_name || ""
  );
  const [signature, setSignature] = React.useState(initialData.signature);
  const [firstName, setFirstName] = React.useState(
    initialData.patient_info.first_name
  );
  const [lastName, setLastName] = React.useState(
    initialData.patient_info.last_name
  );
  const [dob, setDob] = React.useState(initialData.patient_info.dob);
  const [gender, setGender] = React.useState(
    initialData.patient_info.gender || ""
  );
  const [patientPhone, setPatientPhone] = React.useState(
    initialData.patient_info.phone || ""
  );
  const [insuranceCarrier, setInsuranceCarrier] = React.useState(
    initialData.patient_info.insurance_carrier || ""
  );
  const [orderDate, setOrderDate] = React.useState(initialData.order_date);
  const [icdCodes, setIcdCodes] = React.useState(
    initialData.icd_codes?.join(", ") || ""
  );
  const [tests, setTests] = React.useState<Test[]>(initialData.tests || []);
  const [additionalInfo, setAdditionalInfo] = React.useState(
    initialData.additional_info?.join("\n") || ""
  );
  const [resultsExplanation, setResultsExplanation] = React.useState(
    initialData.results_explanation || ""
  );
  const [comments, setComments] = React.useState("");

  // Handle test field changes
  const handleTestChange = (
    index: number,
    field: keyof Test,
    value: string
  ) => {
    const updatedTests = [...tests];
    updatedTests[index] = { ...updatedTests[index], [field]: value };
    setTests(updatedTests);
  };

  // Add new test
  const handleAddTest = () => {
    setTests([
      ...tests,
      {
        test_type: "",
        test_description: "",
        test_reason: "",
        notes: "",
        evidence: "",
      },
    ]);
  };

  // Remove test
  const handleRemoveTest = (index: number) => {
    const updatedTests = tests.filter((_, i) => i !== index);
    setTests(updatedTests);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const correctedData: ExtractedData = {
      physician_name: physicianName || null,
      physician_title: physicianTitle || null,
      physician_npi: physicianNpi || null,
      physician_phone: physicianPhone || null,
      facility_name: facilityName || null,
      signature,
      patient_info: {
        first_name: firstName,
        last_name: lastName,
        dob,
        gender: gender || null,
        phone: patientPhone || null,
        insurance_carrier: insuranceCarrier || null,
      },
      order_date: orderDate,
      icd_codes: icdCodes ? icdCodes.split(",").map((c) => c.trim()).filter(Boolean) : [],
      tests: tests.filter((test) => test.test_type && test.test_description),
      additional_info: additionalInfo ? additionalInfo.split("\n").filter(Boolean) : [],
      results_explanation: resultsExplanation,
      document_type: initialData.document_type,
      confidence: initialData.confidence,
      warnings: initialData.warnings,
    };

    onSave(correctedData, comments);
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          {/* Header */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Correct OCR Results
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and correct any errors in the extracted data below.
            </Typography>
          </Box>

          <Divider />

          {/* Physician Information */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Physician Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Facility Name"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Physician Name"
                value={physicianName}
                onChange={(e) => setPhysicianName(e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Physician Title"
                value={physicianTitle}
                onChange={(e) => setPhysicianTitle(e.target.value)}
                fullWidth
                size="small"
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="NPI"
                  value={physicianNpi}
                  onChange={(e) => setPhysicianNpi(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Phone"
                  value={physicianPhone}
                  onChange={(e) => setPhysicianPhone(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={signature}
                    onChange={(e) => setSignature(e.target.checked)}
                  />
                }
                label="Signature Present"
              />
            </Box>
          </Box>

          <Divider />

          {/* Patient Information */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Patient Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Date of Birth"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="YYYY-MM-DD"
                />
                <TextField
                  label="Order Date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="YYYY-MM-DD"
                />
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Patient Phone"
                  value={patientPhone}
                  onChange={(e) => setPatientPhone(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>
              <TextField
                label="Insurance Carrier"
                value={insuranceCarrier}
                onChange={(e) => setInsuranceCarrier(e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Divider />

          {/* ICD Codes */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              ICD / Diagnosis Codes
            </Typography>
            <TextField
              label="ICD Codes (comma-separated)"
              value={icdCodes}
              onChange={(e) => setIcdCodes(e.target.value)}
              fullWidth
              size="small"
              placeholder="Z12.31, R92.323, C83.18"
            />
          </Box>

          <Divider />

          {/* Tests */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Tests Ordered
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTest}
                size="small"
                variant="outlined"
              >
                Add Test
              </Button>
            </Box>
            {tests.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {tests.map((test, index) => (
                  <Card
                    key={index}
                    variant="outlined"
                    sx={{ bgcolor: "action.hover" }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Test {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveTest(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box
                        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                      >
                        <TextField
                          label="Test Type"
                          value={test.test_type}
                          onChange={(e) =>
                            handleTestChange(index, "test_type", e.target.value)
                          }
                          required
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Test Description"
                          value={test.test_description}
                          onChange={(e) =>
                            handleTestChange(
                              index,
                              "test_description",
                              e.target.value
                            )
                          }
                          required
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                        />
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField
                            label="Body Part"
                            value={test.body_part || ""}
                            onChange={(e) =>
                              handleTestChange(index, "body_part", e.target.value)
                            }
                            fullWidth
                            size="small"
                          />
                          <TextField
                            label="Laterality"
                            value={test.laterality || ""}
                            onChange={(e) =>
                              handleTestChange(index, "laterality", e.target.value)
                            }
                            fullWidth
                            size="small"
                            placeholder="left / right / bilateral"
                          />
                          <TextField
                            label="Contrast"
                            value={test.contrast || ""}
                            onChange={(e) =>
                              handleTestChange(index, "contrast", e.target.value)
                            }
                            fullWidth
                            size="small"
                            placeholder="with / without"
                          />
                        </Box>
                        <TextField
                          label="ICD Code"
                          value={test.icd_code || ""}
                          onChange={(e) =>
                            handleTestChange(index, "icd_code", e.target.value)
                          }
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Test Reason"
                          value={test.test_reason || ""}
                          onChange={(e) =>
                            handleTestChange(index, "test_reason", e.target.value)
                          }
                          fullWidth
                          size="small"
                        />
                        <TextField
                          label="Notes"
                          value={test.notes || ""}
                          onChange={(e) =>
                            handleTestChange(index, "notes", e.target.value)
                          }
                          fullWidth
                          size="small"
                          multiline
                          rows={2}
                        />
                        <TextField
                          label="Evidence"
                          value={test.evidence || ""}
                          onChange={(e) =>
                            handleTestChange(index, "evidence", e.target.value)
                          }
                          fullWidth
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No tests added. Click "Add Test" to add a test.
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Additional Information */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Additional Information
            </Typography>
            <TextField
              label="Additional notes (one per line)"
              value={additionalInfo}
              onChange={(e) => setAdditionalInfo(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder="Handwritten notes, margin annotations, etc."
            />
          </Box>

          <Divider />

          {/* Results Explanation */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Results Explanation
            </Typography>
            <TextField
              label="Results Explanation"
              value={resultsExplanation}
              onChange={(e) => setResultsExplanation(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={3}
            />
          </Box>

          <Divider />

          {/* Correction Comments */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Correction Comments
            </Typography>
            <TextField
              label="Explain what was corrected and why"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              required
              fullWidth
              size="small"
              multiline
              rows={3}
              placeholder="Describe the corrections you made..."
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              pt: 2,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Corrections"}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default OcrCorrectionForm;
